/**
 * Graph API client for SharePoint list operations.
 *
 * Pure service layer — no React imports.
 * Uses @microsoft/microsoft-graph-client with token injection from MSAL.
 * Manually follows @odata.nextLink for pagination (not PageIterator).
 */

import { Client, RetryHandlerOptions } from '@microsoft/microsoft-graph-client';
import type { GraphRequest } from '@microsoft/microsoft-graph-client';
import type { SharePointListItem } from '../types/sharepoint';
import { SHAREPOINT_SITE_URL } from '../config/sharepoint';
import { parseRetryAfter, readHeader } from '../lib/batch';
import type { GraphBatchRequest, GraphBatchResponse } from '../lib/batch';

// Re-exported: the header helpers and batch shapes live in the pure `lib/batch.ts`.
export { parseRetryAfter, readHeader };
export type { GraphBatchRequest, GraphBatchResponse };

// ---------------------------------------------------------------------------
// Custom error class
// ---------------------------------------------------------------------------

export class GraphApiError extends Error {
  public readonly statusCode: number;
  public readonly requestId: string;
  /** On a 429 / 503: the server's `Retry-After`, in ms, when it sent one. */
  public readonly retryAfterMs?: number;

  constructor(statusCode: number, message: string, requestId = '', retryAfterMs?: number) {
    super(message);
    this.name = 'GraphApiError';
    this.statusCode = statusCode;
    this.requestId = requestId;
    if (retryAfterMs !== undefined) this.retryAfterMs = retryAfterMs;
  }
}

// ---------------------------------------------------------------------------
// Headers + Retry-After (SharePoint throttles with 429 OR 503, each with Retry-After)
// ---------------------------------------------------------------------------

/** The Retry-After (ms) carried by a thrown error: our own `retryAfterMs`, else its headers. */
export function retryAfterOf(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const e = err as { retryAfterMs?: unknown; headers?: unknown };
  if (typeof e.retryAfterMs === 'number' && Number.isFinite(e.retryAfterMs)) return e.retryAfterMs;
  return parseRetryAfter(readHeader(e.headers, 'retry-after'));
}

/** The HTTP status on a thrown error: Graph's `statusCode` or a fetch-style `status`. */
export function errorStatus(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null;
  const e = err as { statusCode?: unknown; status?: unknown };
  if (typeof e.statusCode === 'number') return e.statusCode;
  if (typeof e.status === 'number') return e.status;
  return null;
}

// ---------------------------------------------------------------------------
// Error handling helper
// ---------------------------------------------------------------------------

/** Convert a Graph SDK error (or anything thrown) into a `GraphApiError`, without throwing. */
export function toGraphApiError(error: unknown, context: string): GraphApiError {
  if (error instanceof GraphApiError) return error;
  return buildGraphApiError(error, context);
}

function handleGraphError(error: unknown, context: string): never {
  throw buildGraphApiError(error, context);
}

function buildGraphApiError(error: unknown, context: string): GraphApiError {
  // Graph SDK wraps errors with statusCode and body. Its `headers` is a fetch `Headers`
  // object (GraphErrorHandler), not a record, so header reads go through `readHeader`.
  const err = error as {
    statusCode?: number;
    body?: string;
    message?: string;
    headers?: unknown;
  };

  const statusCode = err.statusCode ?? 500;
  const requestId = readHeader(err.headers, 'request-id') ?? '';

  // Parse body for detailed message
  let detail = err.message ?? 'Unknown Graph API error';
  if (err.body) {
    try {
      const body = JSON.parse(err.body) as {
        error?: { message?: string };
      };
      if (body.error?.message) {
        detail = body.error.message;
      }
    } catch {
      // body is not JSON, use as-is
      detail = err.body;
    }
  }

  if (statusCode === 401 || statusCode === 403) {
    return new GraphApiError(
      statusCode,
      `${context}: Token expired or insufficient permissions — ${detail}`,
      requestId,
    );
  }

  if (statusCode === 404) {
    return new GraphApiError(
      statusCode,
      `${context}: Not found — ${detail}`,
      requestId,
    );
  }

  if (statusCode === 429 || statusCode === 503) {
    const retryAfterMs = retryAfterOf(err);
    const shown = retryAfterMs === undefined ? 'unknown' : String(retryAfterMs / 1000);
    console.warn(
      `[GraphApiError] ${statusCode} ${statusCode === 429 ? 'Too Many Requests' : 'Service Unavailable'} (retry-after: ${shown})`,
    );
    return new GraphApiError(
      statusCode,
      statusCode === 429
        ? `${context}: Rate limited — retry after ${shown}s`
        : `${context}: Service unavailable — retry after ${shown}s — ${detail}`,
      requestId,
      retryAfterMs,
    );
  }

  return new GraphApiError(statusCode, `${context}: ${detail}`, requestId);
}

// ---------------------------------------------------------------------------
// Retry (429 / 503 only), honouring Retry-After
// ---------------------------------------------------------------------------

/** Statuses `withRetry` retries by default. Never 412 / 400 / 401 / 403 / 404. */
export const RETRYABLE_STATUSES: readonly number[] = [429, 503];

export type Sleep = (ms: number) => Promise<void>;

export const realSleep: Sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export interface RetryOptions {
  /** Total attempts, including the first. Default 4. */
  maxAttempts?: number;
  /** Injected so tests never wait. Default `setTimeout`. */
  sleep?: Sleep;
  /** Jitter source in [0, 1). Default `Math.random`. */
  random?: () => number;
  /**
   * Which statuses to retry. Default `RETRYABLE_STATUSES` (429, 503). A non-idempotent POST
   * passes `[429]`: a 429 was refused before processing, while a 503 is not guaranteed to be.
   */
  retryOn?: readonly number[];
  /**
   * A `Retry-After` longer than this is not slept on: the error is rethrown at once, carrying
   * `retryAfterMs`, so the caller can schedule the retry instead of holding the UI. Default 30 s.
   */
  maxWaitMs?: number;
}

/** Backoff when the server sent no Retry-After: 1 s, 2 s, 4 s … plus up to 25 % jitter. */
export function backoffMs(retryIndex: number, random: () => number = Math.random): number {
  const base = 1000 * 2 ** Math.max(0, retryIndex);
  return Math.round(base * (1 + 0.25 * random()));
}

/**
 * Run `fn`, retrying only on `retryOn` statuses (429 / 503). Waits `Retry-After` when the error
 * carries one, else backs off exponentially with jitter. Gives up after `maxAttempts`, rethrowing
 * the LAST error unchanged.
 */
export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const maxAttempts = Math.max(1, opts.maxAttempts ?? 4);
  const sleep = opts.sleep ?? realSleep;
  const random = opts.random ?? Math.random;
  const retryOn = opts.retryOn ?? RETRYABLE_STATUSES;
  const maxWaitMs = opts.maxWaitMs ?? 30_000;

  for (let attempt = 1; ; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      const status = errorStatus(err);
      if (status === null || !retryOn.includes(status) || attempt >= maxAttempts) throw err;
      const wait = retryAfterOf(err) ?? backoffMs(attempt - 1, random);
      if (wait > maxWaitMs) throw err;
      await sleep(wait);
    }
  }
}

/**
 * The Graph SDK's default middleware already retries 429 / 503 / 504 (3 times, for POST and
 * PATCH too). Requests that go through `withRetry` switch it off, so `withRetry` is the single,
 * tested retry policy and a non-idempotent POST is never re-sent behind our back.
 */
function noSdkRetry(): RetryHandlerOptions[] {
  return [new RetryHandlerOptions(0, 0)];
}

// ---------------------------------------------------------------------------
// Writes with concurrency control (Phase 13)
// ---------------------------------------------------------------------------

/** The eTag Graph returns on an item or a fieldValueSet. */
export function eTagOf(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  const v = b['@odata.etag'] ?? b.eTag;
  return typeof v === 'string' && v !== '' ? v : undefined;
}

export interface UpdateFieldsResult {
  /** The item's new eTag, for the next write on the same item. */
  eTag?: string;
  /** The fieldValueSet Graph returned. */
  fields: Record<string, unknown>;
}

/**
 * PATCH `…/items/{id}/fields` with `If-Match: <eTag>`. A changed row returns 412, thrown as a
 * `GraphApiError` with `statusCode = 412` and never retried. 429 / 503 are retried per `retry`.
 *
 * `updateItem` (no If-Match) is deliberately left as it was.
 */
export async function updateItemFields(
  client: Client,
  siteId: string,
  listId: string,
  itemId: number | string,
  fields: Record<string, unknown>,
  eTag: string,
  retry?: RetryOptions,
): Promise<UpdateFieldsResult> {
  if (!eTag) throw new GraphApiError(400, `updateItemFields(${itemId}): an eTag is required (If-Match)`);
  const context = `updateItemFields(${itemId})`;
  const body = await withRetry(async () => {
    try {
      return (await client
        .api(`/sites/${siteId}/lists/${listId}/items/${itemId}/fields`)
        .header('If-Match', eTag)
        .middlewareOptions(noSdkRetry())
        .patch(fields)) as Record<string, unknown> | undefined;
    } catch (error) {
      throw toGraphApiError(error, context);
    }
  }, retry);
  return { eTag: eTagOf(body), fields: body ?? {} };
}

/**
 * A list item as Graph returns it, envelope kept: `@odata.etag`, `createdBy` / `lastModifiedBy`
 * (the delegated user SharePoint stamped), `createdDateTime` and `fields`. Graph OMITS an empty
 * field entirely, so a missing key in `fields` means null.
 */
export interface GraphListItemEnvelope {
  id: string;
  '@odata.etag'?: string;
  eTag?: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  createdBy?: { user?: { email?: string; displayName?: string } };
  lastModifiedBy?: { user?: { email?: string; displayName?: string } };
  fields?: Record<string, unknown>;
}

/** The header Graph needs to `$filter` / `$orderby` a non-indexed SharePoint column. */
export const PREFER_NON_INDEXED = 'HonorNonIndexedQueriesWarningMayFailRandomly';

export interface EnvelopeQuery {
  /** Columns for `$expand=fields($select=…)` (never the refused `$select=fields/X`). */
  select?: readonly string[];
  filter?: string;
  top?: number;
  retry?: RetryOptions;
}

/**
 * Read every item of a list with its envelope, following `@odata.nextLink`. The Prefer header is
 * sent on EVERY page (a filtered next page is the same non-indexed query).
 */
export async function listItemEnvelopes(
  client: Client,
  siteId: string,
  listId: string,
  query: EnvelopeQuery = {},
): Promise<GraphListItemEnvelope[]> {
  const out: GraphListItemEnvelope[] = [];
  const context = `listItemEnvelopes(${listId})`;
  type Page = { value?: GraphListItemEnvelope[]; '@odata.nextLink'?: string };
  const fetchPage = (build: () => GraphRequest): Promise<Page> =>
    withRetry(async () => {
      try {
        return (await build()
          .header('Prefer', PREFER_NON_INDEXED)
          .middlewareOptions(noSdkRetry())
          .get()) as Page;
      } catch (error) {
        throw toGraphApiError(error, context);
      }
    }, query.retry);

  let page = await fetchPage(() => {
    let r = client.api(`/sites/${siteId}/lists/${listId}/items`).expand(listItemsExpand(query.select));
    if (query.filter) r = r.filter(query.filter);
    if (query.top) r = r.top(query.top);
    return r;
  });
  out.push(...(page.value ?? []));
  while (page['@odata.nextLink']) {
    const next = page['@odata.nextLink'];
    page = await fetchPage(() => client.api(next));
    out.push(...(page.value ?? []));
  }
  return out;
}

/** Read one item with its envelope (`$expand=fields($select=…)`). */
export async function getItemEnvelope(
  client: Client,
  siteId: string,
  listId: string,
  itemId: number | string,
  select?: readonly string[],
  retry?: RetryOptions,
): Promise<GraphListItemEnvelope> {
  const context = `getItemEnvelope(${itemId})`;
  return withRetry(async () => {
    try {
      return (await client
        .api(`/sites/${siteId}/lists/${listId}/items/${itemId}`)
        .expand(listItemsExpand(select))
        .middlewareOptions(noSdkRetry())
        .get()) as GraphListItemEnvelope;
    } catch (error) {
      throw toGraphApiError(error, context);
    }
  }, retry);
}

/** Escape a single quote for an OData string literal (doubled). */
export function odataLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/** POST one list item (`{ fields }`), retried per `retry` (pass `retryOn: [429]` for audit rows). */
export async function createItemFields(
  client: Client,
  siteId: string,
  listId: string,
  fields: Record<string, unknown>,
  retry?: RetryOptions,
): Promise<{ id: string; eTag?: string }> {
  const context = 'createItemFields';
  const body = await withRetry(async () => {
    try {
      return (await client
        .api(`/sites/${siteId}/lists/${listId}/items`)
        .middlewareOptions(noSdkRetry())
        .post({ fields })) as { id?: unknown } & Record<string, unknown>;
    } catch (error) {
      throw toGraphApiError(error, context);
    }
  }, retry);
  return { id: String(body?.id ?? ''), eTag: eTagOf(body) };
}

/**
 * POST a JSON `$batch` (at most 20 requests) and return its `responses`. The top-level 200 says
 * nothing about each request: partition the result (`lib/batch.ts`). A top-level 429 / 503 is
 * retried per `retry`.
 */
export async function postBatch(
  client: Client,
  requests: readonly GraphBatchRequest[],
  retry?: RetryOptions,
): Promise<GraphBatchResponse[]> {
  if (requests.length === 0) return [];
  if (requests.length > 20) {
    throw new GraphApiError(400, `postBatch: ${requests.length} requests; Graph allows 20 per batch`);
  }
  const body = await withRetry(async () => {
    try {
      return (await client
        .api('/$batch')
        .middlewareOptions(noSdkRetry())
        .post({ requests })) as { responses?: GraphBatchResponse[] } | undefined;
    } catch (error) {
      throw toGraphApiError(error, 'postBatch');
    }
  }, retry);
  return Array.isArray(body?.responses) ? body.responses : [];
}

// ---------------------------------------------------------------------------
// Client factory
// ---------------------------------------------------------------------------

/**
 * Create a Graph API client with token injection.
 *
 * @param getToken - Async function that returns a valid access token
 *                   (typically useAuth().getGraphToken bound to a ref)
 */
export function createGraphClient(
  getToken: () => Promise<string>,
): Client {
  return Client.init({
    authProvider: (done) => {
      getToken()
        .then((token) => done(null, token))
        .catch((err: unknown) =>
          done(err, null),
        );
    },
  });
}

// ---------------------------------------------------------------------------
// Caches (module-level: site and list IDs are stable)
// ---------------------------------------------------------------------------

/**
 * ISS-207 (13-29): one lookup per session, shared. Every caller of `getSiteId` / `getListId` gets the same promise,
 * the in-flight one included, so the shell, each register hook and Today's section no longer each resolve the site
 * (6x) and every list (5x) on first load. Keyed by the site path and by `<siteId>:<list title>`. A failed lookup is
 * forgotten, so the next caller asks again (an unprovisioned list, a sign-in that expired).
 */
const siteIdCache = new Map<string, Promise<string>>();
const listIdCache = new Map<string, Promise<string>>();

/** Forget every resolved id (tests; a sign-out could too). */
export function resetResolutionCaches(): void {
  siteIdCache.clear();
  listIdCache.clear();
}

function shared<T>(cache: Map<string, Promise<T>>, key: string, load: () => Promise<T>): Promise<T> {
  const hit = cache.get(key);
  if (hit) return hit;
  const p = load();
  cache.set(key, p);
  p.catch(() => {
    if (cache.get(key) === p) cache.delete(key);
  });
  return p;
}

// ---------------------------------------------------------------------------
// Site and list resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the SharePoint site ID from the configured site URL.
 * One lookup per session, shared by concurrent callers (ISS-207).
 */
export function getSiteId(client: Client): Promise<string> {
  return shared(siteIdCache, SHAREPOINT_SITE_URL, async () => {
    try {
      const site = (await client.api(`/sites/${SHAREPOINT_SITE_URL}`).get()) as { id: string };
      return site.id;
    } catch (error) {
      handleGraphError(error, 'getSiteId');
    }
  });
}

/**
 * Resolve a list ID by display name within a site.
 * One lookup per site and list title per session, shared by concurrent callers (ISS-207).
 */
export function getListId(client: Client, siteId: string, listName: string): Promise<string> {
  return shared(listIdCache, `${siteId}:${listName}`, () => resolveListId(client, siteId, listName));
}

async function resolveListId(client: Client, siteId: string, listName: string): Promise<string> {
  try {
    // Try display name filter first
    const response = (await client
      .api(`/sites/${siteId}/lists`)
      .filter(`displayName eq '${listName}'`)
      .select('id,displayName')
      .get()) as { value: Array<{ id: string; displayName: string }> };

    if (response.value.length) return response.value[0].id;
  } catch {
    // Display name filter failed — fall through to direct access
  }

  // Fallback: try accessing list by name directly (works for lists whose
  // internal name may differ from the display name)
  try {
    const list = (await client
      .api(`/sites/${siteId}/lists/${listName}`)
      .select('id,displayName')
      .get()) as { id: string; displayName: string };

    return list.id;
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, `getListId(${listName})`);
  }
}

// ---------------------------------------------------------------------------
// CRUD operations
// ---------------------------------------------------------------------------

/** Options for listItems query */
export interface ListItemsOptions {
  filter?: string;
  orderby?: string;
  top?: number;
  select?: string[];
}

/** Result of a listItems call */
export interface ListItemsResult<T> {
  items: T[];
  nextLink?: string;
}

/**
 * The `$expand` clause for a list-items read.
 *
 * Column selection goes INSIDE the expand — `fields($select=A,B)`. The
 * earlier shape, `$expand=fields&$select=id,fields/A,fields/B`, is refused
 * by Graph for EVERY field set (400 "Parsing OData Select and Expand failed:
 * Found a path with multiple navigation properties or a bad complex property
 * path in a select clause", measured 2026-09-22 with `fields/Title` alone),
 * so the Lookup index — this branch's only caller — had never built in
 * production (10-06 Task 3 browser smoke; fixed there).
 */
export function listItemsExpand(select?: readonly string[]): string {
  const cols = (select ?? []).map((s) => s.trim()).filter(Boolean);
  return cols.length ? `fields($select=${cols.join(',')})` : 'fields';
}

/**
 * Fetch list items with automatic pagination.
 * Collects all pages by following @odata.nextLink.
 *
 * @typeParam T - The typed business object (e.g., Shift, Attendance)
 */
export async function listItems<T>(
  client: Client,
  siteId: string,
  listId: string,
  options?: ListItemsOptions,
): Promise<ListItemsResult<T>> {
  const allItems: T[] = [];

  try {
    let request = client
      .api(`/sites/${siteId}/lists/${listId}/items`)
      .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
      .expand(listItemsExpand(options?.select));

    if (options?.filter) request = request.filter(options.filter);
    if (options?.orderby) request = request.orderby(options.orderby);
    if (options?.top) request = request.top(options.top);

    let response = (await request.get()) as {
      value: SharePointListItem[];
      '@odata.nextLink'?: string;
    };

    // The envelope's `createdBy` rides along (fields only carry `AuthorLookupId`); register
    // Own-only scoping keys off its email.
    for (const item of response.value) {
      allItems.push({ id: item.id, ...item.fields, createdBy: item.createdBy } as T);
    }

    // Follow pagination links
    while (response['@odata.nextLink']) {
      response = (await client
        .api(response['@odata.nextLink'])
        .get()) as {
        value: SharePointListItem[];
        '@odata.nextLink'?: string;
      };

      for (const item of response.value) {
        allItems.push({ id: item.id, ...item.fields, createdBy: item.createdBy } as T);
      }
    }

    return { items: allItems };
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, 'listItems');
  }
}

/**
 * Get a single list item by ID.
 */
export async function getItem<T>(
  client: Client,
  siteId: string,
  listId: string,
  itemId: string,
): Promise<T> {
  try {
    const response = (await client
      .api(`/sites/${siteId}/lists/${listId}/items/${itemId}`)
      .expand('fields')
      .get()) as SharePointListItem;

    return { id: response.id, ...response.fields } as T;
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, `getItem(${itemId})`);
  }
}

/**
 * Create a new list item.
 *
 * @param fields - Business fields to write (no SharePoint-internal fields)
 * @returns The created item with its SharePoint-assigned ID
 */
export async function createItem<T>(
  client: Client,
  siteId: string,
  listId: string,
  fields: Record<string, unknown>,
): Promise<T> {
  try {
    const response = (await client
      .api(`/sites/${siteId}/lists/${listId}/items`)
      .post({ fields })) as SharePointListItem;

    return { id: response.id, ...response.fields } as T;
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, 'createItem');
  }
}

/**
 * Update an existing list item's fields.
 *
 * @param fields - Only the fields to update (partial update)
 */
export async function updateItem(
  client: Client,
  siteId: string,
  listId: string,
  itemId: string,
  fields: Record<string, unknown>,
): Promise<void> {
  try {
    await client
      .api(
        `/sites/${siteId}/lists/${listId}/items/${itemId}/fields`,
      )
      .patch(fields);
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, `updateItem(${itemId})`);
  }
}

/**
 * Delete a list item.
 */
export async function deleteItem(
  client: Client,
  siteId: string,
  listId: string,
  itemId: string,
): Promise<void> {
  try {
    await client
      .api(`/sites/${siteId}/lists/${listId}/items/${itemId}`)
      .delete();
  } catch (error) {
    if (error instanceof GraphApiError) throw error;
    handleGraphError(error, `deleteItem(${itemId})`);
  }
}
